import { TrashIcon } from '@heroicons/react/24/outline'
import { GlobalConfigType } from 'actions'
import cloneDeep from 'clone-deep'
import { LayoutLoading } from 'components/LayoutLoading'
import { LoanFieldProps } from 'config'
import { accountTypes } from 'config/user.type.constants'
import { getAvailablePagePermissions } from 'hooks/hasPagePermissions'
import { useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import { setAdminConfig } from 'services'
import { capitalizeLettersSeparatedBySlash } from 'utils'
import { getLoanFieldSubMenus } from 'utils/loanFieldSubMenus.util'
import { RenderInput } from 'utils/RenderInput'

import { SubmenuItem, subMenus } from './constant'

interface FeatureDetailProps {
  featureKey: string
  featureData: {
    title: string
    url: string
    order: number
    value: boolean
    permissions: string[]
    loanStatus: string[]
    submenus?: (string | SubmenuItem)[]
  }
  allData: Record<string, any>
  onBack: () => void
  onUpdate: (key: string, data: any) => void
}

export const FeatureDetail = ({ featureKey, featureData, allData, onBack, onUpdate }: FeatureDetailProps) => {
  const [pagePermissionConfig, loanGlobalStatus, loanFields] = useSelector((state: any) => [
    state.globalConfigReducer[GlobalConfigType.PagePermissions],
    state.loanGlobalStatus,
    state.globalConfigReducer[GlobalConfigType.LoanFields] as LoanFieldProps,
  ])

  const [isLoading, setIsLoading] = useState(false)
  const [localData, setLocalData] = useState(featureData)

  const availableSubmenusByPlan = useMemo(() => {
    return getAvailablePagePermissions()
  }, [pagePermissionConfig])

  const loanFieldSubMenus = useMemo(() => getLoanFieldSubMenus(loanFields), [loanFields])

  const allSubMenus: Record<
    string,
    {
      title: string
    }
  > = useMemo(() => ({ ...subMenus, ...loanFieldSubMenus }), [loanFieldSubMenus])

  const isSubmenuAllowed = (submenuKey: string) =>
    !!loanFieldSubMenus[submenuKey] || availableSubmenusByPlan.includes(submenuKey)

  // Normalize submenus to new format (array of objects)
  const normalizeSubmenus = (submenus?: (string | SubmenuItem)[]): SubmenuItem[] => {
    if (!submenus) return []
    return submenus.map((item) => {
      if (typeof item === 'string') {
        return { key: item, isExternal: false }
      }
      return item
    })
  }

  const normalizedSubmenus = normalizeSubmenus(localData.submenus).filter((item) => isSubmenuAllowed(item.key))

  const handleToggleChange = (submenuKey: string, value: boolean) => {
    const temp = cloneDeep(localData)
    const currentSubmenus = normalizeSubmenus(temp.submenus)

    if (value) {
      // Add submenu if not already present
      if (!currentSubmenus.find((item) => item.key === submenuKey)) {
        temp.submenus = [...currentSubmenus, { key: submenuKey, isExternal: false }]
      }
    } else {
      // Remove submenu
      temp.submenus = currentSubmenus.filter((item) => item.key !== submenuKey)
    }

    setLocalData(temp)
  }

  const handleExternalToggle = (submenuKey: string, isExternal: boolean) => {
    const temp = cloneDeep(localData)
    const currentSubmenus = normalizeSubmenus(temp.submenus)

    temp.submenus = currentSubmenus.map((item) => {
      if (item.key === submenuKey) {
        return { ...item, isExternal }
      }
      return item
    })

    setLocalData(temp)
  }

  const handleTitleChange = (submenuKey: string, title: string) => {
    const temp = cloneDeep(localData)
    const currentSubmenus = normalizeSubmenus(temp.submenus)

    temp.submenus = currentSubmenus.map((item) => {
      if (item.key === submenuKey) {
        // If title is empty or matches default, remove custom title
        const defaultTitle = allSubMenus[submenuKey]?.title
        if (!title.trim() || title === defaultTitle) {
          const next = { ...item }
          delete next.title
          return next
        }
        return { ...item, title }
      }
      return item
    })

    setLocalData(temp)
  }

  const handleMoveUp = (submenuKey: string) => {
    const temp = cloneDeep(localData)
    const currentSubmenus = normalizeSubmenus(temp.submenus)
    const index = currentSubmenus.findIndex((item) => item.key === submenuKey)

    if (index > 0) {
      const newSubmenus = [...currentSubmenus]
      // Swap with previous item
      ;[newSubmenus[index - 1], newSubmenus[index]] = [newSubmenus[index], newSubmenus[index - 1]]
      temp.submenus = newSubmenus
      setLocalData(temp)
    }
  }

  const handleSubmenuLoanStatusChange = (submenuKey: string, selected: Record<string, boolean>) => {
    const temp = cloneDeep(localData)
    const currentSubmenus = normalizeSubmenus(temp.submenus)
    temp.submenus = currentSubmenus.map((item) => {
      if (item.key !== submenuKey) return item
      return { ...item, loanStatus: selected }
    })
    setLocalData(temp)
  }

  const handleSubmenuAccountTypesChange = (submenuKey: string, selected: Record<string, boolean>) => {
    const temp = cloneDeep(localData)
    const currentSubmenus = normalizeSubmenus(temp.submenus)
    temp.submenus = currentSubmenus.map((item) => {
      if (item.key !== submenuKey) return item
      return { ...item, accountTypes: selected }
    })
    setLocalData(temp)
  }

  const handleMoveDown = (submenuKey: string) => {
    const temp = cloneDeep(localData)
    const currentSubmenus = normalizeSubmenus(temp.submenus)
    const index = currentSubmenus.findIndex((item) => item.key === submenuKey)

    if (index !== -1 && index < currentSubmenus.length - 1) {
      const newSubmenus = [...currentSubmenus]
      // Swap with next item
      ;[newSubmenus[index], newSubmenus[index + 1]] = [newSubmenus[index + 1], newSubmenus[index]]
      temp.submenus = newSubmenus
      setLocalData(temp)
    }
  }

  const handleSave = async () => {
    setIsLoading(true)
    const updatedAllData = { ...allData, [featureKey]: localData }
    await setAdminConfig('features', updatedAllData)
    onUpdate(featureKey, localData)
    setIsLoading(false)
  }

  const availableSubmenus = useMemo(
    () =>
      Object.entries(allSubMenus).filter(
        ([submenuKey]) => !normalizedSubmenus.find((item) => item.key === submenuKey) && isSubmenuAllowed(submenuKey),
      ),
    [normalizedSubmenus, allSubMenus, loanFieldSubMenus, availableSubmenusByPlan],
  )

  return (
    <div className="relative px-3 py-1.5">
      <LayoutLoading show={isLoading} />

      <div className="flex items-center justify-between mb-4">
        <div className="text-xl font-bold">{capitalizeLettersSeparatedBySlash(featureKey)} - Submenus</div>
        <div className="flex gap-2">
          <button onClick={onBack} className="px-4 py-2 bg-gray-500 text-white rounded hover:bg-gray-600">
            Back
          </button>
          <button onClick={handleSave} className="px-4 py-2 bg-primary text-white rounded hover:bg-blue-600">
            Save
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Selected Submenus with Order */}
        <div>
          <h3 className="text-lg font-semibold mb-3">Selected Submenus (In Order)</h3>
          <div className="space-y-2">
            {normalizedSubmenus.length === 0 ? (
              <div className="text-gray-500 italic p-3 bg-gray-50 rounded">No submenus selected</div>
            ) : (
              normalizedSubmenus.map((submenuItem, index) => {
                const submenuValue = allSubMenus[submenuItem.key]
                if (!submenuValue) return null

                const isFirst = index === 0
                const isLast = index === normalizedSubmenus.length - 1

                return (
                  <div
                    key={submenuItem.key}
                    className="flex items-start gap-2 p-3 bg-blue-50 border border-blue-200 rounded"
                  >
                    <div className="flex flex-col gap-1 pt-1">
                      <button
                        onClick={() => handleMoveUp(submenuItem.key)}
                        disabled={isFirst}
                        className={`px-2 py-1 text-xs rounded ${
                          isFirst
                            ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                            : 'bg-primary text-white hover:bg-blue-600'
                        }`}
                        title="Move Up"
                      >
                        ▲
                      </button>
                      <button
                        onClick={() => handleMoveDown(submenuItem.key)}
                        disabled={isLast}
                        className={`px-2 py-1 text-xs rounded ${
                          isLast
                            ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                            : 'bg-primary text-white hover:bg-blue-600'
                        }`}
                        title="Move Down"
                      >
                        ▼
                      </button>
                    </div>
                    <div className="w-[80%] flex-1">
                      <div className="flex items-center justify-between gap-2 mb-2">
                        <div className="font-medium">
                          {index + 1}. {submenuItem.title || submenuValue.title}
                        </div>
                        <span
                          className="text-red-500 p-1 hover-shadow1 cursor-pointer"
                          onClick={() => handleToggleChange(submenuItem.key, false)}
                          title="Remove"
                        >
                          <TrashIcon className="w-4 h-4" />
                        </span>
                      </div>
                      <div className="pl-4 space-y-2">
                        <RenderInput
                          input={{
                            inputType: 'Text',
                            title: 'Custom Title (optional)',
                            value: submenuItem.title || '',
                            placeholder: submenuValue.title,
                          }}
                          Key={`title-${submenuItem.key}`}
                          onChange={(_: string, value: string) => handleTitleChange(submenuItem.key, value)}
                        />
                        <RenderInput
                          input={{
                            inputType: 'checkbox',
                            title: 'External Link (opens in new tab)',
                            value: submenuItem.isExternal || false,
                          }}
                          Key={`external-${submenuItem.key}`}
                          onChange={(_: string, value: boolean) => handleExternalToggle(submenuItem.key, value)}
                        />
                        <RenderInput
                          input={{
                            inputType: 'multiselect',
                            title: 'Loan Status',
                            options: loanGlobalStatus,
                            value: submenuItem.loanStatus || {},
                            defaultSelected: true,
                          }}
                          Key={`${submenuItem.key}-loanStatus`}
                          onChange={(_: string, value: Record<string, boolean>) =>
                            handleSubmenuLoanStatusChange(submenuItem.key, value)
                          }
                        />
                        <RenderInput
                          input={{
                            inputType: 'multiselect',
                            title: 'Allowed account types',
                            options: accountTypes,
                            value: submenuItem.accountTypes || {},
                            sort: true,
                            sortKey: 'value',
                            defaultSelected: true,
                          }}
                          Key={`${submenuItem.key}-accountTypes`}
                          onChange={(_: string, value: Record<string, boolean>) =>
                            handleSubmenuAccountTypesChange(submenuItem.key, value)
                          }
                        />
                      </div>
                    </div>
                  </div>
                )
              })
            )}
          </div>
        </div>

        {/* Available Submenus */}
        <div>
          <h3 className="text-lg font-semibold mb-3">Available Submenus</h3>
          <div className="space-y-2">
            {availableSubmenus.length === 0 ? (
              <div className="text-gray-500 italic p-3 bg-gray-50 rounded">All submenus are selected</div>
            ) : (
              availableSubmenus.map(([submenuKey, submenuValue]) => (
                <div key={submenuKey} className="flex items-center justify-between p-3 bg-gray-50 rounded">
                  <div className="flex-1">
                    <div className="font-medium">{submenuValue.title}</div>
                  </div>
                  <button
                    onClick={() => handleToggleChange(submenuKey, true)}
                    className="px-3 py-1 bg-green-500 text-white text-sm rounded hover:bg-green-600"
                  >
                    Add
                  </button>
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
